import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2, FileUp, IndianRupee, Loader2, Paperclip, Plus, ScanText, Trash2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { cn } from "@/lib/utils";

const TONE = { "Pending Approval": "warning", Approved: "success", Rejected: "danger" };
const CATEGORIES = ["Train tickets", "Flight tickets", "Bus / taxi", "Accommodation",
  "Food", "Local travel", "Material", "Other"];

const blank = {
  category: "Train tickets", vendor: "", date: new Date().toISOString().slice(0, 10),
  amount: "", tax: "", description: "", reference: "", bill_data: "", bill_name: "",
};

/** Travel and stay bills for a demo, with bill scanning and admin approval. */
export const DemoExpenses = ({ demo, onChanged }) => {
  const { isAdmin, can } = useAuth();
  const canEdit = can("demos", "edit");
  const [rows, setRows] = useState(demo.expenses || []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bill, setBill] = useState(null);
  const fileRef = useRef();

  useEffect(() => { setRows(demo.expenses || []); }, [demo]);

  const reload = async () => {
    const { data } = await api.get(`/demos/${demo.id}`);
    setRows(data.expenses || []);
    onChanged?.();
  };

  const attach = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Please choose a bill smaller than 5 MB.");
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, bill_data: reader.result, bill_name: f.name }));
    reader.readAsDataURL(f);
  };

  const scan = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/demos/expenses/scan-bill", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      const reader = new FileReader();
      reader.onload = () => setForm((s) => ({
        ...s, category: data.category || s.category, vendor: data.vendor || "",
        date: data.date || s.date, amount: data.amount || "", tax: data.tax || "",
        description: data.description || "", reference: data.reference || "",
        bill_data: reader.result, bill_name: f.name,
      }));
      reader.readAsDataURL(f);
      toast.success(`Bill read: ${data.vendor || "vendor"} \u00b7 ${money(data.amount)}`);
    } catch (err) {
      toast.error(errMsg(err, "The bill could not be read. Please type the details in."));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!Number(form.amount)) return toast.error("Enter the amount on the bill.");
    setBusy(true);
    try {
      await api.post(`/demos/${demo.id}/expenses`, {
        ...form, amount: Number(form.amount), tax: Number(form.tax || 0),
      });
      toast.success("Expense added and sent for approval");
      setForm(blank);
      setOpen(false);
      reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const decide = async (row, approve) => {
    const remarks = approve ? "" : window.prompt("Why is this expense rejected?") || "";
    if (!approve && !remarks.trim()) return toast.error("A remark is needed to reject an expense.");
    try {
      await api.post(`/demos/expenses/${row.id}/decide?approve=${approve}`, { remarks });
      toast.success(`${row.number} ${approve ? "approved" : "rejected"}`);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const viewBill = async (row) => {
    try {
      const { data } = await api.get(`/demos/bills/${row.bill_id}`);
      setBill({ ...data, number: row.number });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete expense ${row.number}?`)) return;
    try {
      await api.delete(`/demos/expenses/${row.id}`);
      toast.success("Expense deleted");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const approved = rows.filter((r) => r.status === "Approved").reduce((a, r) => a + Number(r.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
        <div className="num text-sm">
          <span className="text-muted-foreground">Claimed</span> <b>{money(total)}</b>
          <span className="mx-2 text-border">|</span>
          <span className="text-muted-foreground">Approved</span> <b className="text-success">{money(approved)}</b>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="add-expense-btn">
            <Plus className="h-3.5 w-3.5" /> Add expense
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No travel or stay bills recorded for this visit yet.
        </p>
      ) : rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-border bg-card p-3" data-testid={`expense-${r.number}`}>
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{r.category}
                <span className="ml-2 num text-xs font-normal text-muted-foreground">{r.number}</span>
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {[r.vendor, r.description, r.reference].filter(Boolean).join(" \u00b7 ")}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {fdate(r.date)} &middot; by {r.created_by_name}
                {r.decision_by_name ? ` \u00b7 ${r.status.toLowerCase()} by ${r.decision_by_name}` : ""}
                {r.decision_remarks ? ` \u00b7 ${r.decision_remarks}` : ""}
              </p>
            </div>
            <div className="text-right">
              <p className="num text-sm font-bold">{money(r.amount)}</p>
              <StatusBadge status={r.status} tone={TONE[r.status]} />
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {r.bill_id && (
              <Button size="sm" variant="ghost" onClick={() => viewBill(r)} data-testid={`view-bill-${r.number}`}>
                <Paperclip className="h-3.5 w-3.5" /> View bill
              </Button>
            )}
            {isAdmin && r.status === "Pending Approval" && (
              <>
                <Button size="sm" variant="success" onClick={() => decide(r, true)} data-testid={`approve-exp-${r.number}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => decide(r, false)}
                  data-testid={`reject-exp-${r.number}`}>
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
              </>
            )}
            {isAdmin && (
              <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => remove(r)}
                data-testid={`delete-exp-${r.number}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}

      <p className="text-[11px] text-muted-foreground">
        {canEdit
          ? "Bill photos are kept for 60 days and then deleted automatically. The amounts stay on record for ever."
          : "You have read-only access to these expenses."}
      </p>

      {/* Add expense */}
      <Dialog open={open && canEdit} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add an expense</DialogTitle>
            <DialogDescription>
              Scan the bill and we fill in the vendor, date and amount for you. Everything stays editable.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/25 bg-primary-soft/40 p-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium hover:border-primary/40">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <ScanText className="h-4 w-4 text-primary" />}
                {scanning ? "Reading the bill\u2026" : "Scan bill (photo or PDF)"}
                <input ref={fileRef} type="file" hidden accept="image/*,application/pdf"
                  disabled={scanning} onChange={scan} data-testid="scan-bill-input" />
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-primary">
                <FileUp className="h-3.5 w-3.5" /> attach without scanning
                <input type="file" hidden accept="image/*,application/pdf" onChange={attach} data-testid="attach-bill-input" />
              </label>
              {form.bill_name && <StatusBadge status={form.bill_name} tone="success" dot={false} />}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Category" required>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Vendor / airline / hotel">
                <Input value={form.vendor} data-testid="exp-vendor"
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
              </Field>
              <Field label="Bill date">
                <Input type="date" value={form.date} data-testid="exp-date"
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field label="Ticket / PNR / bill no.">
                <Input value={form.reference} className="num" data-testid="exp-reference"
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
              <Field label="Amount paid" required>
                <Input type="number" min="0" step="0.01" required value={form.amount} data-testid="exp-amount"
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label="GST in the bill">
                <Input type="number" min="0" step="0.01" value={form.tax} data-testid="exp-tax"
                  onChange={(e) => setForm({ ...form, tax: e.target.value })} />
              </Field>
            </div>

            <Field label="Details">
              <Input value={form.description} data-testid="exp-desc"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ahmedabad to Pune, 2 passengers" />
            </Field>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="exp-cancel">Cancel</Button>
              <Button type="submit" disabled={busy || scanning} data-testid="exp-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
                Save expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bill viewer */}
      <Dialog open={!!bill} onOpenChange={(v) => !v && setBill(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Bill {bill?.number}</DialogTitle>
            <DialogDescription>{bill?.name} &middot; uploaded {fdate(bill?.uploaded_at)}</DialogDescription>
          </DialogHeader>
          {bill && (bill.mime?.includes("pdf") ? (
            <a href={bill.data} target="_blank" rel="noreferrer" data-testid="bill-open-pdf" className="text-sm font-semibold text-primary underline">
              Open the PDF bill
            </a>
          ) : (
            <img src={bill.data} alt="bill" className={cn("w-full rounded-lg border border-border")} />
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};
